import { useEffect, useState } from "react";
import { WhatsAppAlerts } from "@/components/WhatsAppAlerts";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const GROUPS = [
  { title: "Strategy", fields: [["enabled", "LVRC strategy on", "bool"], ["universe", "Stocks", ["all", "nifty50"]], ["instrument", "Trade in", ["cash", "futures"]]] },
  { title: "1 · A/D bias", fields: [["ad_long", "Long if A/D ≥"], ["ad_short", "Short if A/D ≤"], ["ad_universe", "A/D stock list", ["all_fo", "sector_list"]], ["ad_recheck", "Re-check at 09:30 if 09:15 gives no trade (move since 09:15)", "bool"], ["recheck_long", "Re-check: Long if A/D ≥"], ["recheck_short", "Re-check: Short if A/D ≤"]] },
  { title: "3 · Stocks", fields: [["top_n", "Stocks per day", "int"]] },
  { title: "5 · Lowest vol / re-entry", fields: [["lowvol_broken_only", "Only range-broken stocks set the low", "bool"], ["strict_lowest", "Quietest candle itself must be the reversal", "bool"], ["reset_low_after_trade", "Reset the low after each trade", "bool"], ["reset_low_after_cancel", "Reset the low after a cancelled/expired order", "bool"], ["allow_reentry", "Allow re-entry in the same stock", "bool"]] },
  { title: "6 · Order", fields: [["auto_entry", "Trade mode: place entries automatically (no Y)", "bool"], ["entry_ticks", "Trigger ticks beyond signal", "int"], ["limit_ticks", "Limit ticks beyond trigger", "int"], ["order_valid_candles", "Valid for N candles", "int"], ["cancel_on", "Cancel when signal low/high", ["touch", "close"]], ["fill_start", "Fills from", "time"], ["fill_end", "Fills until", "time"]] },
  { title: "7 · Stop-loss", fields: [["stop_mode", "Stop mode", ["prev_candle", "or_range", "signal_candle"]], ["stop_buffer_pct", "Cushion beyond SL candle %"], ["stop_min_pct", "Skip if stop < %"], ["stop_max_pct", "Skip if stop > %"]] },
  { title: "8 · Exit", fields: [["rr_partial", "Partial at R"], ["partial_pct", "Partial %", "int"], ["eod", "All out at", "time"]] },
  { title: "Position sizing", fields: [["sizing", "Method", ["trade_value", "risk_capped", "fixed_risk", "pct_capital", "fixed_qty"]], ["mis_leverage", "MIS leverage ×"], ["trade_value", "Position value ₹ / trade"], ["risk_amount", "Fixed risk ₹ / trade"], ["capital", "Capital ₹"], ["risk_pct", "Risk % of capital"], ["fixed_lots", "Fixed lots / stock", "int"], ["round_to_lot", "Round qty to F&O lot", "bool"], ["charges", "Deduct Zerodha charges", "bool"]] },
  { title: "Run limits (per day)", fields: [["max_trades", "Max trades", "int"], ["max_losses", "Stop after N losses", "int"], ["max_open", "Max open positions", "int"], ["max_daily_loss", "Daily loss cap ₹ (0 = off)"]] },
];


const OPT_LABEL = {
  all_fo: "All F&O stocks", sector_list: "Sector list only",
  all: "All sector stocks (list below)", nifty50: "Nifty 50 only", cash: "Cash (equity MIS)", futures: "Stock futures (lots)",
  touch: "Is traded through", close: "Closes beyond",
  prev_candle: "Previous candle", or_range: "Opening range", signal_candle: "Signal candle",
  trade_value: "Position value ₹", risk_capped: "Risk ÷ stop, capped by capital × leverage", fixed_risk: "Fixed ₹ risk", pct_capital: "% of capital", fixed_qty: "Fixed lots",
};
const LABELS = Object.fromEntries(GROUPS.flatMap((g) => g.fields.map(([k, l]) => [k, l])));
const fieldErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => `${LABELS[x.loc?.[x.loc.length - 1]] || x.loc?.slice(-1)}: ${x.msg}`).join(" · ");
  return errMsg(e);
};

function Field({ k, label, type, value, onChange }) {
  const cls = "mt-1 w-full h-9 rounded-md bg-[#0B111D] border border-[#1E293B] px-2 text-sm num text-slate-200";
  if (Array.isArray(type)) return (
    <label className="text-[11px] text-slate-400">{label}
      <select value={value} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={`setting-${k}`}>{type.map((o) => <option key={o} value={o}>{OPT_LABEL[o] || o}</option>)}</select>
    </label>
  );
  if (type === "bool") return (
    <label className="text-[11px] text-slate-400 flex items-center gap-2 mt-5">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={`setting-${k}`} className="accent-cyan-400 h-4 w-4" />{label}
    </label>
  );
  return (
    <label className="text-[11px] text-slate-400">{label}
      <input type={type === "time" ? "text" : "number"} step="any" value={value ?? ""} data-testid={`setting-${k}`}
        onChange={(e) => onChange(type === "time" ? e.target.value : type === "int" ? parseInt(e.target.value || "0", 10) : parseFloat(e.target.value))} className={cls} />
    </label>
  );
}

export default function Settings() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-slate-100">Settings</h1>
      </div>
      <WhatsAppAlerts />
      <LvrcSettings />
    </div>
  );
}

function LvrcSettings() {
  const [s, setS] = useState(null);
  const [sectorText, setSectorText] = useState("");
  useEffect(() => {
    api.get("/settings").then((r) => {
      setS(r.data);
      setSectorText(Object.entries(r.data.sectors).map(([k, v]) => `${k}: ${v.join(", ")}`).join("\n"));
    });
  }, []);
  if (!s) return <div className="label-xs p-6">Loading…</div>;
  const set = (k) => (v) => setS({ ...s, [k]: v });
  const save = async () => {
    const sectors = Object.fromEntries(sectorText.split("\n").filter((l) => l.includes(":")).map((l) => {
      const [k, v] = l.split(":");
      return [k.trim(), v.split(",").map((x) => x.trim()).filter(Boolean)];
    }));
    const { defaults, ...body } = s;
    try { const r = await api.put("/settings", { ...body, sectors }); setS(r.data); toast.success("Settings saved"); }
    catch (e) { toast.error(fieldErr(e)); }
  };
  return (
    <div className="space-y-4" data-testid="settings-page">
      <div className="flex items-center gap-3">
        <p className="text-xs text-slate-400">Lowest-Volume Reversal Candle (5-min, A/D + sector)</p>
        <button onClick={save} data-testid="settings-save-btn" className="ml-auto h-10 px-5 rounded-md bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">Save</button>
      </div>
      <p className="text-xs text-amber-300/90">Run limits are starting values (3 trades a day, stop after 2 losses, one trade at a time). Adjust them to your own rules.</p>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {GROUPS.map((g) => (
          <div key={g.title} className="panel p-4">
            <div className="label-xs mb-2">{g.title}</div>
            <div className="grid grid-cols-2 gap-3">
              {g.fields.map(([k, label, type]) => <Field key={k} k={k} label={label} type={type} value={s[k]} onChange={set(k)} />)}
            </div>
          </div>
        ))}
      </div>
      <div className="panel p-4">
        <div className="label-xs mb-1">2 · Sectors (F&O members)</div>
        <p className="text-[11px] text-slate-500 mb-2">One sector per line: <span className="num">NAME: SYM1, SYM2, …</span> — Kite trading symbols. Used for A/D only when "A/D stock list" is set to Sector list only.</p>
        <textarea value={sectorText} onChange={(e) => setSectorText(e.target.value)} rows={14} data-testid="setting-sectors"
          className="w-full rounded-md bg-[#0B111D] border border-[#1E293B] p-3 text-xs num text-slate-200" />
      </div>
    </div>
  );
}
